import { type HTMLAttributes, type ReactNode, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import type { IconDefinition } from '@fortawesome/fontawesome-common-types';
import { faCheck } from '@fortawesome/pro-light-svg-icons';
import { faCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { type ClassValue } from 'clsx';
import type { UUID } from 'crypto';

import { formatRelativeDate } from '@spacefill/shared/src/utils/Date';
import { Tooltip } from '@spacefill/uikit/src/components/Tooltip';

import { cn } from '../../lib/utils';
import { UserFullname } from '../UserFullname';

export interface InAppNotificationProps {
    titleIcon: IconDefinition;
    user?: {
        firstName: string;
        lastName: string;
        company?: string;
        customer?: { company: string };
        logisticProvider?: { company: string };
        isService: boolean;
        deletedAt: string | null;
    } | null;
    createdFromRuleId?: UUID;
    receivedAt: Date;
    highlighted?: boolean;
    unread?: boolean;
    onMarkAsRead?: () => void;
    onMarkAsUnread?: () => void;
    isNotificationChild?: boolean;
    className?: ClassValue;
    dataTestid?: string;
    htmlAttributes?: HTMLAttributes<HTMLDivElement>;
    children: ReactNode;
}

export const InAppNotification = ({
    titleIcon,
    isNotificationChild = false,
    user,
    createdFromRuleId,
    highlighted = false,
    receivedAt,
    unread = true,
    onMarkAsRead,
    onMarkAsUnread,
    dataTestid,
    className,
    htmlAttributes,
    children,
}: InAppNotificationProps) => {
    const { t } = useTranslation();

    useEffect(() => {
        if (highlighted && unread && onMarkAsRead) {
            onMarkAsRead();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // only on mount

    const company = user?.customer?.company || user?.logisticProvider?.company || user?.company;

    return (
        <div
            data-testid={dataTestid}
            {...htmlAttributes}
            data-component-name='InAppNotification'
            className={cn(
                'group flex items-stretch gap-3 px-2 py-3 hover:bg-gray-50 border-b border-solid border-gray-200',
                !highlighted && !unread && !isNotificationChild && 'bg-gray-100',
                highlighted && 'bg-lime-50',
                className
            )}
        >
            <div className='mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-500'>
                <FontAwesomeIcon icon={titleIcon} />
            </div>
            <div className='min-w-0 flex-1 text-sm'>
                {user && (
                    <div className='text-gray-500'>
                        {createdFromRuleId ? (
                            `${t('Automation rule')} · ${company}`
                        ) : user.isService ? (
                            company
                        ) : (
                            <>
                                <UserFullname
                                    dataTestid={dataTestid && `${dataTestid}-name`}
                                    firstName={user.firstName}
                                    lastName={user.lastName}
                                    isDeleted={!!user.deletedAt}
                                />
                                {company && (user.firstName || user.lastName) ? ' · ' : ''}
                                {company || ''}
                            </>
                        )}
                    </div>
                )}

                <div
                    data-testid={dataTestid && `${dataTestid}-content`}
                    className='mb-0.5'
                >
                    {children}
                </div>
            </div>
            <div className='flex shrink-0 flex-col items-end justify-between gap-1.5'>
                <div className='grow flex items-center'>
                    {!isNotificationChild && unread ? (
                        <Tooltip
                            content={t('Mark as read')}
                            position='top'
                        >
                            <button
                                data-dd-action-name='mark-notification-as-read'
                                type='button'
                                onClick={(event) => {
                                    event.stopPropagation();
                                    event.preventDefault();
                                    onMarkAsRead?.();
                                }}
                                data-testid={dataTestid && `${dataTestid}-indicator`}
                                className='flex cursor-pointer p-2'
                            >
                                <FontAwesomeIcon
                                    icon={faCircle}
                                    className='text-xs rounded-full text-red-500 group-hover:outline-1 group-hover:outline-zinc-600 group-hover:outline-offset-2'
                                />
                            </button>
                        </Tooltip>
                    ) : !isNotificationChild ? (
                        <Tooltip
                            content={t('Mark as unread')}
                            position='top'
                        >
                            <button
                                data-dd-action-name='mark-notification-as-unread'
                                type='button'
                                onClick={(event) => {
                                    event.stopPropagation();
                                    event.preventDefault();
                                    onMarkAsUnread?.();
                                }}
                                data-testid={dataTestid && `${dataTestid}-indicator`}
                                className='flex cursor-pointer p-2 items-center justify-center rounded-full text-gray-400 hover:text-gray-600'
                            >
                                <FontAwesomeIcon
                                    icon={faCheck}
                                    className='text-xs'
                                />
                            </button>
                        </Tooltip>
                    ) : null}
                </div>
                <span className='text-xs text-gray-400'>{formatRelativeDate(receivedAt)}</span>
            </div>
        </div>
    );
};
